/**
 * Tenant Context
 * Provides tenant configuration and theme to the application
 * Handles multi-tenancy awareness for the frontend
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from '../utils/apiClient';
import { getAvailableThemes, setTenantThemeConfig } from '../utils/themeSelector';
import { generateThemeMetadata } from '../utils/themeGenerator';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

/**
 * Default tenant configuration (Master)
 * Master is always ID 1 in production deployments
 */
const DEFAULT_TENANT = {
  id: 1,
  code: 'master',
  name: 'Master',
  systemName: 'Capital Management System',
  theme: {
    name: 'default',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    logoUrl: null,
  },
  modules: [
    'capital_management',
    'contract_management',
    'payments',
    'stakeholder_management',
    'reports',
    'reference_data',
    'user_management',
    'admin_settings',
    'tenant_management',
  ],
  roundingPrecision: 3,
};

/**
 * Master tenant configuration - has access to all modules including tenant management
 */
const MASTER_TENANT_MODULES = [
  'capital_management',
  'contract_management',
  'payments',
  'stakeholder_management',
  'reports',
  'reference_data',
  'user_management',
  'admin_settings',
  'tenant_management',
  'amortization_schedules',
];

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-tenant workflow document requirements (workflow_code -> is_required).
  // Loaded once per tenant and consulted by upload dialogs to decide whether
  // to render the "submit without document" affordance.
  const [workflowDocReqs, setWorkflowDocReqs] = useState(null);

  /**
   * Load tenant configuration from API
   */
  const loadTenantConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch current tenant config
      const response = await apiGet('/tenants/config/current');

      if (response.success && response.data) {
        const tenantData = response.data;

        // If this is the master tenant, ensure all admin modules are enabled
        if (tenantData.code === 'master' || tenantData.code === 'default') {
          tenantData.modules = MASTER_TENANT_MODULES;
        }

        setTenant(tenantData);

        // IMPORTANT: Sync localStorage with the resolved tenant to prevent desync
        // This ensures subsequent API calls use the same tenant the API resolved
        const currentSelectedId = localStorage.getItem('selectedTenantId');
        if (tenantData.id && currentSelectedId !== String(tenantData.id)) {
          // Only update if different - prevents unnecessary writes
          // Use tenant ID (not code) for consistency with X-Tenant-ID header
          localStorage.setItem('selectedTenantId', String(tenantData.id));
          console.log(`Synced selectedTenantId to ${tenantData.id} (${tenantData.code})`);
        }

        // Cache the tenant theme for themeSelector to use
        if (tenantData.theme) {
          try {
            setTenantThemeConfig(tenantData.theme);
          } catch (themeErr) {
            console.warn('Failed to set tenant theme config:', themeErr);
          }
        }
        console.log('Tenant loaded:', tenantData.code, tenantData);
      } else {
        // Use default tenant - API returned no tenant context
        // This usually means tenants haven't been configured yet
        setTenant(DEFAULT_TENANT);
        console.log('Using default tenant configuration (no tenant resolved from API)');
      }
    } catch (err) {
      console.warn('Failed to load tenant config, using default:', err.message);
      setTenant(DEFAULT_TENANT);
      setError(null); // Don't show error, just use default
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load tenant config on mount
   */
  useEffect(() => {
    loadTenantConfig();
  }, [loadTenantConfig]);

  /**
   * Load workflow document requirements once tenant resolves. Failure here
   * is non-fatal — every workflow simply falls back to "not required" until
   * the data lands, which mirrors the seed defaults (only contract_submit is
   * mandatory by default and that's enforced on the server side anyway).
   */
  useEffect(() => {
    if (!tenant?.id) return;
    let cancelled = false;
    apiGet('/workflow-document-requirements')
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          const map = {};
          res.data.forEach((row) => { map[row.workflow_code] = row.is_required === true; });
          setWorkflowDocReqs(map);
        } else {
          setWorkflowDocReqs({});
        }
      })
      .catch(() => { if (!cancelled) setWorkflowDocReqs({}); });
    return () => { cancelled = true; };
  }, [tenant?.id]);

  /**
   * Returns true when the named workflow currently requires a document
   * upload. Defaults to false while the requirements map is still loading
   * or when the workflow code is not registered.
   */
  const isWorkflowDocumentRequired = useCallback((workflowCode) => {
    if (!workflowCode || !workflowDocReqs) return false;
    return workflowDocReqs[workflowCode] === true;
  }, [workflowDocReqs]);

  /**
   * Check if a module is enabled for this tenant
   */
  const isModuleEnabled = useCallback((moduleCode) => {
    if (!tenant?.modules) return true; // Default to enabled
    return tenant.modules.includes(moduleCode);
  }, [tenant]);

  /**
   * Check if a specific sub-feature is enabled for a module.
   * Defaults to enabled if no config exists (backwards compatible).
   * When a parent key (e.g. "brokers") is false but child keys
   * (e.g. "brokers.basic_information") are true, the parent is
   * treated as enabled so tabs/sections remain visible.
   */
  const isFeatureEnabled = useCallback((moduleCode, featureKey) => {
    if (!tenant?.modules) return true;
    if (!tenant.modules.includes(moduleCode)) return false;
    if (!tenant.moduleConfig?.[moduleCode]) return true;

    const config = tenant.moduleConfig[moduleCode];

    // If the direct key is not explicitly false, it's enabled
    if (config[featureKey] !== false) return true;

    // Direct key is false — check if ANY child features are still enabled
    const prefix = featureKey + '.';
    return Object.entries(config).some(
      ([key, val]) => key.startsWith(prefix) && val === true
    );
  }, [tenant]);

  /**
   * Get list of enabled modules
   */
  const getEnabledModules = useCallback(() => {
    return tenant?.modules || DEFAULT_TENANT.modules;
  }, [tenant]);

  /**
   * Resolve a per-tenant terminology label by key, falling back when unset.
   * Backed by tenant_settings rows with key like 'stakeholder.<type>.label'.
   */
  const getStakeholderLabel = useCallback(
    (typeKey, fallback) => tenant?.terminology?.[typeKey] || fallback,
    [tenant]
  );

  /**
   * Get tenant theme configuration
   */
  const getTheme = useCallback(() => {
    return tenant?.theme || DEFAULT_TENANT.theme;
  }, [tenant]);


  /**
   * Get available themes for user profile selection
   * Order: Default, Dark Mode, then Tenant theme (if configured)
   */
  const availableThemes = useMemo(() => {
    try {
      const defaultThemes = getAvailableThemes(); // Returns [Default, Dark Mode]

      // If tenant has a custom theme configuration, add it after Dark Mode
      if (tenant?.theme?.primaryColor) {
        const tenantThemeMeta = generateThemeMetadata(tenant.theme, tenant.name);
        if (tenantThemeMeta) {
          // Order: Default, Dark Mode, Tenant theme
          return [...defaultThemes, tenantThemeMeta];
        }
      }

      return defaultThemes;
    } catch (err) {
      console.warn('Failed to compute available themes:', err);
      return getAvailableThemes();
    }
  }, [tenant]);

  /**
   * Switch to a different tenant (for super-admins)
   * Note: The primary tenant switching is handled in Navbar.js using setSelectedTenantId()
   * This function is kept for backwards compatibility but should use the same key
   */
  const switchTenant = useCallback(async (tenantId) => {
    try {
      setLoading(true);
      // Store tenant preference using the correct localStorage key
      // This must match what apiClient.js reads (selectedTenantId)
      localStorage.setItem('selectedTenantId', tenantId);
      // Reload the page to apply new tenant context
      window.location.reload();
    } catch (err) {
      setError('Failed to switch tenant');
      setLoading(false);
    }
  }, []);

  const value = {
    tenant,
    loading,
    error,
    isModuleEnabled,
    isFeatureEnabled,
    getEnabledModules,
    getTheme,
    availableThemes,
    terminology: tenant?.terminology || {},
    getStakeholderLabel,
    isWorkflowDocumentRequired,
    switchTenant,
    reload: loadTenantConfig,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * HOC to require a specific module
 * Wraps a component and only renders if the module is enabled
 */
export const withModuleRequired = (WrappedComponent, moduleCode) => {
  return function ModuleRequiredComponent(props) {
    const { isModuleEnabled } = useTenant();

    if (!isModuleEnabled(moduleCode)) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Feature Not Available</h2>
          <p>This feature is not available for your organization.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Component that only renders children if module is enabled
 */
export const ModuleGate = ({ module, children, fallback = null }) => {
  const { isModuleEnabled, loading } = useTenant();

  if (loading) {
    return null;
  }

  if (!isModuleEnabled(module)) {
    return fallback;
  }

  return children;
};

export default TenantContext;
